import React from 'react';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import type { MonteCarloResult } from '@/hooks/useEmpMonteCarlo';

interface Props {
  history: MonteCarloResult[];
  maxItems?: number;
}

const sourceLabels: Record<string, string> = {
  manual: 'Manual',
  auto_queue: 'Auto',
  boletim_aprovado: 'Boletim',
  boletim_aprovado_para_faturamento: 'Boletim',
  boletim_rejeitado: 'Rejeição',
  baseline_ativada: 'Baseline',
  contrato_data_fim: 'Prazo',
  aditivo_aprovado: 'Aditivo',
  cronograma_aprovado: 'Cronograma',
  planilha_ativada: 'Planilha',
  validacao_insert: 'Execução',
  validacao_update: 'Execução',
  curva_s_snapshot: 'Snapshot',
  fix_c1_c5: 'Correção',
};

const fmtTS = (iso: string) => {
  try { return format(new Date(iso), "dd/MM HH:mm"); } catch { return iso; }
};

export const McEventTimeline: React.FC<Props> = ({ history, maxItems = 5 }) => {
  if (history.length < 2) return null;

  const items = history.slice(0, maxItems);
  
  // Compute deltas between consecutive versions
  const deltas = items.map((item, idx) => {
    const prev = items[idx + 1]; // history is desc order
    if (!prev) return null;
    return {
      probDelta: item.prob_on_time - prev.prob_on_time,
      p50DaysDelta: item.p50_dias - prev.p50_dias,
    };
  });

  // Overall trend
  const first = items[0];
  const last = items[items.length - 1];
  const overallDelta = first.prob_on_time - last.prob_on_time;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          Histórico de Simulações
        </h4>
        {overallDelta !== 0 && (
          <Badge
            variant={overallDelta > 0 ? 'success' : 'destructive'}
            className="text-[10px] gap-1"
          >
            {overallDelta > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
            {overallDelta > 0 ? '+' : ''}{overallDelta.toFixed(0)}pp em {items.length} versões
          </Badge>
        )}
      </div>

      <div className="relative space-y-0">
        {items.map((item, idx) => {
          const delta = deltas[idx];
          const source = item.triggered_by
            ? sourceLabels[item.triggered_by] || item.triggered_by
            : '—';
          const isActive = item.status === 'ativo';

          return (
            <div key={item.id} className="flex items-start gap-3 py-1.5 group">
              {/* Timeline dot + line */}
              <div className="flex flex-col items-center mt-1">
                <div className={cn(
                  'w-2 h-2 rounded-full border-2 flex-shrink-0',
                  isActive
                    ? 'bg-success border-success'
                    : 'bg-muted border-muted-foreground/30',
                )} />
                {idx < items.length - 1 && (
                  <div className="w-px h-full min-h-[16px] bg-border" />
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 flex items-center gap-2 text-xs">
                <span className="text-muted-foreground tabular-nums w-[72px] flex-shrink-0">
                  {fmtTS(item.created_at)}
                </span>
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 flex-shrink-0">
                  v{item.versao_logica}
                </Badge>
                <span className="text-muted-foreground truncate">{source}</span>
                <span className="ml-auto flex items-center gap-2 flex-shrink-0">
                  <span className="tabular-nums font-medium text-foreground">{item.prob_on_time.toFixed(0)}%</span>
                  {delta && (
                    <span className={cn(
                      'text-[10px] tabular-nums',
                      delta.probDelta > 0 ? 'text-success' : delta.probDelta < 0 ? 'text-destructive' : 'text-muted-foreground',
                    )}>
                      {delta.probDelta > 0 ? '↑' : delta.probDelta < 0 ? '↓' : '—'}
                      {Math.abs(delta.probDelta).toFixed(0)}
                    </span>
                  )}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
